import React, {useContext, useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {PRISMAX_DATA_PIPELINE_URL} from '../../configs/config';
import {SwapMainAppContext} from '../../context/SwapMainAppContext';
import {getApiErrorMessage, isSessionExpiredError} from '../../utils/authErrors';
import styles from './EpisodeDetailModal.module.css';

function formatText(value) {
    const text = String(value ?? '').trim();
    return text || '-';
}

function formatOverviewValue(value) {
    const text = String(value ?? '').trim();
    if (!text) return '-';
    const separator = text.includes(';') ? ';' : ',';
    if (!text.includes(separator)) return text;
    const formatted = text
        .split(separator)
        .map((part) => part.trim())
        .filter(Boolean)
        .join(', ');
    return formatted || '-';
}

function formatCount(value) {
    if (value === null || value === undefined || String(value).trim() === '') return '-';
    const count = Number(value);
    if (!Number.isFinite(count) || count < 0) return '-';
    return Math.round(count).toLocaleString('en-US');
}

function formatSimilarEpisodes(value) {
    if (value === null || value === undefined || String(value).trim() === '') return '-';
    const count = Number(value);
    if (!Number.isFinite(count) || count < 0) return '-';
    if (count >= 100) {
        return `${Math.floor(count / 100) * 100}+`;
    }
    return String(Math.round(count));
}

function formatQuality(value) {
    if (value === null || value === undefined || String(value).trim() === '') return '-';
    const score = Number(value);
    if (!Number.isFinite(score)) return '-';
    return score % 1 === 0 ? String(score) : score.toFixed(1);
}

function formatFileCount(value, fallback) {
    if (value === null || value === undefined || String(value).trim() === '') return fallback;
    const count = Number(value);
    if (!Number.isFinite(count)) return fallback;
    return Math.max(0, Math.round(count));
}

function formatDurationMinutesFromHours(value) {
    if (value === null || value === undefined || String(value).trim() === '') return '-';
    const hours = Number(value);
    if (!Number.isFinite(hours) || hours <= 0) return '-';
    const minutes = hours * 60;
    return `${minutes % 1 === 0 ? minutes.toFixed(0) : minutes.toFixed(1)} min`;
}

function formatDurationClockFromHours(value) {
    if (value === null || value === undefined || String(value).trim() === '') return '-';
    const hours = Number(value);
    if (!Number.isFinite(hours) || hours <= 0) return '-';
    const totalSeconds = Math.max(1, Math.round(hours * 60 * 60));
    const displayHours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (displayHours > 0) {
        return `${displayHours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function FileIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <rect x="2" y="1" width="9" height="13" rx="1.5"/>
            <path d="M5 6h4M5 8.5h4M5 11h2"/>
            <path d="M11 1v4h4" strokeWidth="1.2"/>
        </svg>
    );
}

function McapIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <path d="M2 10h2l2-6 2 9 2-5 1 2h3"/>
        </svg>
    );
}

function DownloadIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M8 2v8m-3-3 3 3 3-3"/>
            <path d="M3 13h10"/>
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M3 8l4 4 6-7"/>
        </svg>
    );
}

function DatasetIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <rect x="1" y="1" width="6" height="6" rx="1"/>
            <rect x="9" y="1" width="6" height="6" rx="1"/>
            <rect x="1" y="9" width="6" height="6" rx="1"/>
            <rect x="9" y="9" width="6" height="6" rx="1"/>
        </svg>
    );
}

function RobotIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <path d="M3 13V7a2 2 0 012-2h2V3a1 1 0 011-1h2a1 1 0 011 1v2h2a2 2 0 012 2v6"/>
            <path d="M1 13h14"/>
        </svg>
    );
}

function getCameraLabel(video) {
    const slot = String(video?.slot || '').trim().toLowerCase();
    const objectName = String(video?.object_name || '').trim().toLowerCase();
    if (slot === 'left' || objectName.includes('left')) return 'left';
    if (slot === 'right' || objectName.includes('right')) return 'right';
    if (objectName.includes('high')) return 'high';
    return slot === 'env' ? 'env' : 'camera';
}

function getCameraOrder(video) {
    const label = getCameraLabel(video);
    if (label === 'left') return 0;
    if (label === 'high' || label === 'env') return 1;
    if (label === 'right') return 2;
    return 3;
}

function getTaskTags(task) {
    const rawTags = task?.tags || task?.task_tags || [];
    if (Array.isArray(rawTags)) {
        return rawTags.map((tag) => String(tag || '').trim()).filter(Boolean);
    }
    return String(rawTags || '')
        .split(/[,;]/)
        .map((tag) => tag.trim())
        .filter(Boolean);
}

function EpisodeDetailModal({
    adminToken = '',
    adminMode = false,
    hideDownload = false,
    episode,
    task,
    scenarioEpisodeCount,
    uploadEpisodeCount,
    taskAverageQuality,
    averageDurationHours,
    isSelected,
    inOrder = false,
    downloadDisabled = false,
    onClose,
    onDownload,
    onToggleSelect,
}) {
    const {gatewayToken, handleUserSessionExpired, handleAdminSessionExpired} = useContext(SwapMainAppContext);
    const [cameraVideos, setCameraVideos] = useState([]);
    const [isLoadingCameras, setIsLoadingCameras] = useState(true);
    const [cameraError, setCameraError] = useState('');
    const [isPlaying, setIsPlaying] = useState(true);
    const [activeCameraIndex, setActiveCameraIndex] = useState(0);
    const videoRefs = useRef([]);
    const cameraStripRef = useRef(null);
    const episodeId = String(episode?.id || episode?.episodeId || '').trim();
    const authToken = adminMode ? adminToken : gatewayToken;

    useEffect(() => {
        if (!episodeId || !authToken) {
            setCameraVideos([]);
            setIsLoadingCameras(false);
            setCameraError('Camera previews are unavailable.');
            return undefined;
        }

        const controller = new AbortController();
        setCameraVideos([]);
        setIsLoadingCameras(true);
        setCameraError('');
        setActiveCameraIndex(0);
        setIsPlaying(true);
        videoRefs.current = [];

        const loadCameraVideos = async () => {
            try {
                const response = await fetch(
                    `${PRISMAX_DATA_PIPELINE_URL}/data/downloadable-episodes/${episodeId}/preview-videos${adminMode ? '?admin=true' : ''}`,
                    {
                        headers: {Authorization: `Bearer ${authToken}`},
                        signal: controller.signal,
                    },
                );
                const json = await response.json().catch(() => ({}));
                if (!response.ok || !json?.success) {
                    throw new Error(getApiErrorMessage(json, 'Failed to load camera previews.'));
                }

                const videos = (Array.isArray(json?.data?.derived_videos) ? json.data.derived_videos : [])
                    .map((video) => ({
                        ...video,
                        url: String(video?.url || video?.signed_url || '').trim(),
                    }))
                    .filter((video) => video.url)
                    .sort((left, right) => getCameraOrder(left) - getCameraOrder(right));
                setCameraVideos(videos);
                if (!videos.length) setCameraError('No camera previews are available for this episode.');
            } catch (error) {
                if (error?.name === 'AbortError') return;
                console.error('Episode camera preview load error:', error);
                setCameraVideos([]);
                setCameraError(error.message || 'Failed to load camera previews.');
                if (isSessionExpiredError(error)) {
                    if (adminMode) handleAdminSessionExpired?.();
                    else handleUserSessionExpired?.();
                }
            } finally {
                if (!controller.signal.aborted) setIsLoadingCameras(false);
            }
        };

        void loadCameraVideos();
        return () => controller.abort();
    }, [adminMode, authToken, episodeId, handleAdminSessionExpired, handleUserSessionExpired]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const root = document.getElementById('root');
    if (!root || !episode) return null;

    const title = formatText(episode.taskTitle || task?.scenario);
    const description = formatText(task?.details_description || task?.description || task?.notes);
    const previewVideoUrl = String(episode.previewVideoUrl || '').trim();
    const previewImageUrl = String(episode.previewImageUrl || '').trim();
    const machineProductName = String(episode.machineProductName || '').trim();
    const isYam = machineProductName.toLowerCase() === 'yam';
    const mp4Count = formatFileCount(episode.mp4Count, '-');
    const mcapCount = formatFileCount(episode.mcapCount, '-');
    const durationLabel = formatDurationClockFromHours(episode.durationHours);
    const tags = getTaskTags(task);
    const overviewRows = [
        {label: 'Objectives', value: formatOverviewValue(task?.objects)},
        {label: 'Action', value: formatOverviewValue(task?.action)},
        {label: 'Variations', value: formatOverviewValue(task?.task_variations)},
        {label: 'Notes', value: formatOverviewValue(task?.notes)},
    ];
    const fallbackVideos = previewVideoUrl ? [{
        slot: 'env',
        object_name: 'preview.mp4',
        url: previewVideoUrl,
    }] : [];
    const displayedVideos = cameraVideos.length ? cameraVideos : (!isLoadingCameras ? fallbackVideos : []);
    const loadingSlots = ['env', 'left', 'right'];

    const handleTogglePlayback = () => {
        const videos = videoRefs.current.filter(Boolean);
        if (!videos.length) return;
        if (isPlaying) {
            videos.forEach((video) => video.pause());
            setIsPlaying(false);
            return;
        }
        videos.forEach((video) => {
            void video.play().catch(() => undefined);
        });
        setIsPlaying(true);
    };

    const handlePrimaryTimeUpdate = (event) => {
        const currentTime = event.currentTarget.currentTime;
        videoRefs.current.slice(1).forEach((video) => {
            if (video?.readyState > 0 && Math.abs(video.currentTime - currentTime) > 0.35) {
                video.currentTime = currentTime;
            }
        });
    };

    const handleCameraScroll = () => {
        const strip = cameraStripRef.current;
        const firstPane = strip?.firstElementChild;
        if (!strip || !firstPane) return;
        const step = firstPane.getBoundingClientRect().width + 2;
        if (!step) return;
        setActiveCameraIndex(Math.max(0, Math.min(displayedVideos.length - 1, Math.round(strip.scrollLeft / step))));
    };

    return ReactDOM.createPortal(
        <div
            className={styles.backdrop}
            onClick={(event) => {
                if (event.target === event.currentTarget) onClose?.();
            }}
        >
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <div className={styles.modalTitleGroup}>
                        <div className={styles.modalTitle}>{title}</div>
                        <div className={styles.modalSubtitle}>
                            <DatasetIcon/>
                            <span>{title}</span>
                            <span className={styles.subtitleDivider}>·</span>
                            <span>{formatCount(scenarioEpisodeCount)} episodes in dataset</span>
                        </div>
                    </div>
                    <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close episode details">
                        <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                            <path d="M2 2l10 10M12 2L2 12"/>
                        </svg>
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.cameraWrap}>
                        <div
                            ref={cameraStripRef}
                            className={styles.cameraStrip}
                            onScroll={handleCameraScroll}
                        >
                            {isLoadingCameras ? loadingSlots.map((slot) => (
                                <div
                                    key={slot}
                                    className={`${styles.cameraPane} ${styles.cameraPaneLoading}`}
                                    role="status"
                                    aria-label={`Loading ${slot} camera preview`}
                                />
                            )) : displayedVideos.length ? displayedVideos.map((video, index) => (
                                <div key={`${video.slot || 'camera'}-${video.object_name || index}`} className={styles.cameraPane}>
                                    <span className={styles.cameraLabel}>{getCameraLabel(video)}</span>
                                    <video
                                        ref={(element) => { videoRefs.current[index] = element; }}
                                        className={`${styles.cameraMedia} ${isYam ? styles.yamPreviewMedia : ''}`}
                                        src={video.url}
                                        muted
                                        autoPlay
                                        loop
                                        playsInline
                                        preload="metadata"
                                        controlsList="nodownload noplaybackrate noremoteplayback"
                                        disablePictureInPicture
                                        onPlay={index === 0 ? () => setIsPlaying(true) : undefined}
                                        onPause={index === 0 ? () => setIsPlaying(false) : undefined}
                                        onTimeUpdate={index === 0 ? handlePrimaryTimeUpdate : undefined}
                                        onContextMenu={(event) => event.preventDefault()}
                                    />
                                </div>
                            )) : (
                                <div className={`${styles.cameraPane} ${styles.cameraPaneEmpty}`}>
                                    {previewImageUrl ? (
                                        <img
                                            className={`${styles.cameraMedia} ${isYam ? styles.yamPreviewMedia : ''}`}
                                            src={previewImageUrl}
                                            alt=""
                                        />
                                    ) : null}
                                    <span className={styles.cameraLoadingText}>{cameraError || 'No camera preview available.'}</span>
                                </div>
                            )}
                        </div>
                        {displayedVideos.length ? (
                            <button
                                type="button"
                                className={styles.cameraPlayButton}
                                onClick={handleTogglePlayback}
                                aria-label={isPlaying ? 'Pause camera videos' : 'Play camera videos'}
                            >
                                {isPlaying ? <span className={styles.pauseIcon}><i/><i/></span> : (
                                    <svg viewBox="0 0 10 10" aria-hidden="true"><polygon points="2,1 9,5 2,9"/></svg>
                                )}
                            </button>
                        ) : null}
                        <span className={styles.cameraDuration}>{durationLabel}</span>
                    </div>
                    <div className={styles.cameraDots} aria-hidden="true">
                        {displayedVideos.map((video, index) => (
                            <span
                                key={`${video.slot || 'camera'}-dot`}
                                className={`${styles.cameraDot} ${index === activeCameraIndex ? styles.cameraDotActive : ''}`}
                            />
                        ))}
                    </div>

                    <div className={styles.metaStrip}>
                        <div className={styles.badgeRow}>
                            <span className={`${styles.badge} ${styles.episodeBadge}`}>Episode {formatText(episode.episodeId)}</span>
                            <span className={`${styles.badge} ${styles.qualityBadge}`}>Score {formatQuality(episode.qualityScore)}</span>
                            <span className={`${styles.badge} ${styles.durationBadge}`}>{durationLabel}</span>
                            {inOrder ? <span className={`${styles.badge} ${styles.orderBadge}`}>In order</span> : null}
                        </div>
                        {machineProductName ? (
                            <div className={styles.machineRow}><RobotIcon/><span>{machineProductName}</span></div>
                        ) : null}
                        <div className={styles.fileRow}>
                            <div className={`${styles.fileChip} ${styles.mp4Chip}`}>
                                <FileIcon/>
                                <span className={styles.fileChipLabel}>MP4</span>
                                <span className={styles.fileChipCount}>×{mp4Count}</span>
                            </div>
                            <div className={`${styles.fileChip} ${styles.mcapChip}`}>
                                <McapIcon/>
                                <span className={styles.fileChipLabel}>MCAP</span>
                                <span className={styles.fileChipCount}>×{mcapCount}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.infoGrid}>
                        <div className={styles.infoColumn}>
                            <div className={styles.sectionLabel}>Description</div>
                            <p className={styles.description}>{description}</p>
                            {tags.length ? (
                                <>
                                    <div className={`${styles.sectionLabel} ${styles.tagsLabel}`}>Tags</div>
                                    <div className={styles.tags}>
                                        {tags.map((tag) => <span key={tag} className={styles.tag}>{tag}</span>)}
                                    </div>
                                </>
                            ) : null}
                        </div>
                        <div className={styles.infoColumn}>
                            <div className={styles.sectionLabel}>Overview</div>
                            <div className={styles.overviewGrid}>
                                {overviewRows.map((row) => (
                                    <div key={row.label} className={styles.overviewField}>
                                        <span className={styles.overviewLabel}>{row.label}</span>
                                        <span className={styles.overviewValue}>{row.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className={styles.infoColumn}>
                            <div className={styles.sectionLabel}>Dataset stats</div>
                            <div className={styles.statGrid}>
                                <div className={styles.statBox}><div className={`${styles.statValue} ${styles.green}`}>{formatSimilarEpisodes(scenarioEpisodeCount)}</div><div className={styles.statLabel}>Scenario total</div></div>
                                <div className={styles.statBox}><div className={styles.statValue}>{formatQuality(taskAverageQuality)}</div><div className={styles.statLabel}>Scenario avg quality</div></div>
                                <div className={styles.statBox}><div className={styles.statValue}>{formatCount(uploadEpisodeCount)}</div><div className={styles.statLabel}>In this batch</div></div>
                                <div className={styles.statBox}><div className={styles.statValue}>{formatDurationMinutesFromHours(averageDurationHours)}</div><div className={styles.statLabel}>Scenario avg duration</div></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <span className={styles.footerNote}>
                        {adminMode
                            ? (inOrder ? 'This episode is already in the selected Order.' : 'Select to add this episode to the selected Order.')
                            : 'Select to include this episode in your download'}
                    </span>
                    <div className={styles.footerButtons}>
                        {!hideDownload ? (
                            <button type="button" className={styles.ghostButton} disabled={downloadDisabled} onClick={() => onDownload?.(episode)}>
                                <DownloadIcon/>
                                Download episode
                            </button>
                        ) : null}
                        <button type="button" className={styles.selectButton} disabled={inOrder} onClick={() => onToggleSelect?.(episode.id)}>
                            <CheckIcon/>
                            {inOrder ? 'In order' : (isSelected ? 'Selected' : 'Select episode')}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        root,
    );
}

export default EpisodeDetailModal;
